import { RecordLink } from 'components/record-chip/RecordLink';
import React from 'react';
import {
    CalculatedPricelist,
    ConnectedMatterDto,
    EmployeeRate,
    HourlyRate,
    PricingKind,
    ScopeType,
} from 'types/pricelists';
import { employeeRowName, isEmployeePriced, pricedEmployeeRows } from './modal/employeePricing';

export const getMattersLinks = (domain: string, listItems: ConnectedMatterDto[]): React.ReactNode[] => {
    const links: React.ReactNode[] = [];
    listItems
        .sort((a: ConnectedMatterDto, b: ConnectedMatterDto) =>
            `${a.matterNumber} ${a.matterName}`.localeCompare(`${b.matterNumber} ${b.matterName}`)
        )
        .forEach((item, index) => {
            const optionalComma: boolean = index < listItems.length - 1;
            const optionalWhiteSpace = index === 0 ? null : ' ';
            const fullName = `${item.matterNumber} ${item.matterName}`;
            links.push(
                <div key={item.matterId} className="mr-2 overflow-hidden text-ellipsis">
                    {optionalWhiteSpace}
                    {/* A case a line: the new design's chips need no comma between them. */}
                    <RecordLink
                        className="entity-link text-fg hover:text-link no-underline hover:underline"
                        to={`/${domain}/${item.matterId}`}
                        label={fullName}
                    >
                        {optionalComma ? `${fullName},` : fullName}
                    </RecordLink>
                </div>
            );
        });
    return links;
};

// Role-priced lists that charge the same rates are one choice, so they collapse on their pricing hash. An
// employee-priced list is its own choice whatever it charges: it has periods the hash does not tell apart.
export function pricelistIdentity(pricelist: CalculatedPricelist): string {
    return isEmployeePriced(pricelist) ? `employee-${pricelist.id}` : pricelist.pricingHash;
}

// What the case editor's picker offers a case of the given kind: an employee-priced case follows a model or contract
// list per employee in its currency, never another case's list; a role-priced case copies role-priced lists.
export function isOfferedInPicker(
    pricelist: Pick<CalculatedPricelist, 'pricingKind' | 'scopeTypeCode' | 'currencyTypeCode'>,
    pricingKind: PricingKind,
    currencyTypeCode: string | null | undefined
): boolean {
    if (currencyTypeCode && pricelist.currencyTypeCode !== currencyTypeCode) {
        return false;
    }
    if (pricingKind === PricingKind.EMPLOYEE) {
        return isEmployeePriced(pricelist) && pricelist.scopeTypeCode !== ScopeType.MATTER;
    }
    return !isEmployeePriced(pricelist);
}

const getUniqueListByIdentity = (array: CalculatedPricelist[], filter: string[]): CalculatedPricelist[] => {
    const arrayUniqueByKey = [...new Map(array.map((item) => [pricelistIdentity(item), item])).values()];

    return arrayUniqueByKey.filter((item) => !filter.includes(pricelistIdentity(item)));
};

export const addUniquePricelistsToOrderedList = (
    pricelists: CalculatedPricelist[],
    orderedList: CalculatedPricelist[]
): void => {
    const uniquePricelists = getUniqueListByIdentity(pricelists, orderedList.map(pricelistIdentity));
    uniquePricelists.forEach((p) => orderedList.push(p));
};

// A named price line of the clipboard copy: a role or, in the personal group, a person. The rates reads
// send the price as a JSON number; a form holds it as a string.
export interface ClipboardRateRow {
    name: string;
    price: string | number | null;
}

// The rich-text half of the copy is markup, so a name or title carrying `<` or `&` has to arrive as text.
const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

export const EMPLOYEE_CLIPBOARD_GROUP = 'EMPLOYEES';

// An employee-priced list's priced people as one clipboard group, "name · role" beside the price.
export function employeeClipboardGroups(rates: EmployeeRate[] | undefined): Record<string, ClipboardRateRow[]> {
    return {
        [EMPLOYEE_CLIPBOARD_GROUP]: pricedEmployeeRows(rates).map((rate) => ({
            name: employeeRowName(rate),
            price: rate.hourlyPrice,
        })),
    };
}

export const buildHourlyRatesClipboardData = (
    data: Record<string, HourlyRate[]>,
    groupTitles: Record<string, string>,
    extraGroups: Record<string, ClipboardRateRow[]> = {}
): { html: string; plainText: string } => {
    const htmlRows: string[] = [];
    const textRows: string[] = [];

    const groups: Record<string, ClipboardRateRow[]> = Object.fromEntries(
        Object.entries(data).map(([groupKey, rates]) => [
            groupKey,
            rates.map((rate) => ({ name: rate.jobRole.internalName ?? rate.jobRole.name, price: rate.hourlyPrice })),
        ])
    );
    Object.assign(groups, extraGroups);

    for (const [groupKey, rows] of Object.entries(groups)) {
        const title = groupTitles[groupKey] || groupKey;
        htmlRows.push(`<tr><td colspan="2"><strong>${escapeHtml(title)}</strong></td></tr>`);
        textRows.push(title);

        rows.forEach((row) => {
            const price = String(row.price ?? '');
            htmlRows.push(`<tr><td>${escapeHtml(row.name)}</td><td>${escapeHtml(price)}</td></tr>`);
            textRows.push(`${row.name}\t${price}`);
        });
    }

    const html = `<table>${htmlRows.join('')}</table>`;
    const plainText = textRows.join('\n');

    return { html, plainText };
};
